import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  WorkoutSection,
  WorkoutSectionType,
} from "@/components/workouts/workoutsColumns";
import {
  WORKOUT_SECTION_LABELS,
  WORKOUT_SECTION_TYPES,
} from "@/lib/utils/workoutSections";
import {
  AlarmClock,
  Dumbbell,
  Flame,
  Infinity,
  RefreshCw,
  Snowflake,
  Timer,
  TimerReset,
  Trash2,
  type LucideIcon,
} from "lucide-react";

export const sectionTypeIcons: Record<WorkoutSectionType, LucideIcon> = {
  standard: Dumbbell,
  warmup: Flame,
  cooldown: Snowflake,
  amrap: Infinity,
  circuit: RefreshCw,
  interval: TimerReset,
  emom: AlarmClock,
  for_time: Timer,
};

const SectionTypeOption = ({ type }: { type: WorkoutSectionType }) => {
  const Icon = sectionTypeIcons[type];
  return (
    <span className="inline-flex items-center gap-2">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span>{WORKOUT_SECTION_LABELS[type]}</span>
    </span>
  );
};

const secondsToMinutes = (seconds: number | null) =>
  seconds == null ? "" : String(Math.round(seconds / 60));

const minutesToSeconds = (value: string) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed * 60);
};

const numberOrNull = (value: string) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed);
};

type WorkoutSectionHeaderProps = {
  section: WorkoutSection;
  sectionIndex: number;
  canDelete: boolean;
  readOnly?: boolean;
  onChange: (section: WorkoutSection) => void;
  onDelete: () => void;
};

const WorkoutSectionHeader = ({
  section,
  sectionIndex,
  canDelete,
  readOnly = false,
  onChange,
  onDelete,
}: WorkoutSectionHeaderProps) => {
  const showRounds =
    section.section_type === "circuit" ||
    section.section_type === "emom" ||
    section.section_type === "interval";
  const showTimeCap =
    section.section_type === "amrap" || section.section_type === "for_time";
  const showInterval = section.section_type === "emom";
  const showWorkRest = section.section_type === "interval";

  return (
    <div className="mb-3 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={section.section_type}
          onValueChange={(value) =>
            onChange({
              ...section,
              section_type: value as WorkoutSectionType,
            })
          }
          disabled={readOnly}
        >
          <SelectTrigger className="h-8 w-[170px] [&>span]:inline-flex [&>span]:items-center">
            <SelectValue>
              <SectionTypeOption type={section.section_type} />
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {WORKOUT_SECTION_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                <SectionTypeOption type={type} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={section.name ?? ""}
          onChange={(event) =>
            onChange({
              ...section,
              name: event.target.value.trim() ? event.target.value : null,
            })
          }
          placeholder={`Section ${sectionIndex + 1} name`}
          className="h-8 min-w-[140px] flex-1"
          disabled={readOnly}
        />
        {canDelete && !readOnly ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      {(showRounds || showTimeCap || showInterval || showWorkRest) && (
        <div className="flex flex-wrap items-center gap-2">
          {showRounds ? (
            <Input
              type="number"
              min={1}
              value={section.rounds ?? ""}
              onChange={(event) =>
                onChange({
                  ...section,
                  rounds: numberOrNull(event.target.value),
                })
              }
              placeholder="Rounds"
              className="h-8 w-24"
              disabled={readOnly}
            />
          ) : null}
          {showTimeCap ? (
            <Input
              type="number"
              min={1}
              value={secondsToMinutes(section.time_cap_seconds)}
              onChange={(event) =>
                onChange({
                  ...section,
                  time_cap_seconds: minutesToSeconds(event.target.value),
                })
              }
              placeholder="Cap (min)"
              className="h-8 w-28"
              disabled={readOnly}
            />
          ) : null}
          {showInterval ? (
            <Input
              type="number"
              min={1}
              value={section.interval_seconds ?? ""}
              onChange={(event) =>
                onChange({
                  ...section,
                  interval_seconds: numberOrNull(event.target.value),
                })
              }
              placeholder="Every (sec)"
              className="h-8 w-28"
              disabled={readOnly}
            />
          ) : null}
          {showWorkRest ? (
            <>
              <Input
                type="number"
                min={1}
                value={section.work_seconds ?? ""}
                onChange={(event) =>
                  onChange({
                    ...section,
                    work_seconds: numberOrNull(event.target.value),
                  })
                }
                placeholder="Work (sec)"
                className="h-8 w-28"
                disabled={readOnly}
              />
              <Input
                type="number"
                min={1}
                value={section.rest_seconds ?? ""}
                onChange={(event) =>
                  onChange({
                    ...section,
                    rest_seconds: numberOrNull(event.target.value),
                  })
                }
                placeholder="Rest (sec)"
                className="h-8 w-28"
                disabled={readOnly}
              />
            </>
          ) : null}
        </div>
      )}
      {!readOnly || (section.notes ?? "").trim().length > 0 ? (
        <Textarea
          value={section.notes ?? ""}
          onChange={(event) =>
            onChange({
              ...section,
              notes: event.target.value.trim() ? event.target.value : null,
            })
          }
          placeholder="Section notes"
          className="min-h-[64px] resize-none border-0 bg-background/70 shadow-none"
          disabled={readOnly}
        />
      ) : null}
    </div>
  );
};

export default WorkoutSectionHeader;
